import { Table, Thead, Tr, Th, Tbody, Td, Button } from '@chakra-ui/react';
import { SortedRoomReportWithTotals } from 'common/interfaces/reports';
import React from 'react';
import { usePagination, useTable } from 'react-table';
import { useReportDownload } from 'api/hooks/ApiHooks';
import { useAuthContext } from 'common/context/authContext';
import Paginator from './paginator';

type Proptypes = {
    tripId: string;
    listData?: SortedRoomReportWithTotals;
};

function RemarksTable(props: Proptypes) {
    const { listData, tripId } = props;

    const data = React.useMemo(() => {
        if (!listData) return [];

        const contacts = listData.rooms.map((room) => room?.contacts).flat();

        const sortedContacts = contacts.sort((a, b) => {
            const lastNameA = a && a.lastName ? a.lastName.toUpperCase() : '';
            const lastNameB = b && b.lastName ? b.lastName.toUpperCase() : '';
            if (lastNameA < lastNameB) {
                return -1;
            }
            if (lastNameA > lastNameB) {
                return 1;
            }
            return 0;
        });

        return sortedContacts.map((contact) => ({
            name: `${contact?.lastName || ''}, ${contact?.firstName || ''}`,
            mobilePhone: contact?.mobilePhone || '',
            remarks: contact?.remarksEng || '',
            allergyRemark: contact?.foodAllergyRemark || '',
        }));
    }, [listData]);

    const { startDownload, loading } = useReportDownload(
        useAuthContext(),
        tripId,
        'Deltakerliste merkander'
    );

    const columns = React.useMemo(
        () => [
            {
                Header: 'Navn',
                accessor: 'name', // accessor is the "key" in the data
                width: 250,
            } as const,
            {
                Header: 'Merknader',
                accessor: 'remarks',
            } as const,
            {
                Header: 'Allergier',
                accessor: 'allergyRemark',
            } as const,
        ],
        []
    );
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data,
            initialState: { pageIndex: 0 },
        },
        usePagination
    );
    return (
        <div>
            <Table variant='striped' {...getTableProps()} mb={4}>
                <Thead>
                    {headerGroups.map((headerGroup) => (
                        <Tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column) => (
                                <Th {...column.getHeaderProps()}>
                                    {column.render('Header')}
                                </Th>
                            ))}
                        </Tr>
                    ))}
                </Thead>
                <Tbody {...getTableBodyProps()}>
                    {page.map((row) => {
                        prepareRow(row);
                        return (
                            <Tr {...row.getRowProps()}>
                                {row.cells.map((cell) => {
                                    return (
                                        <Td
                                            {...cell.getCellProps({
                                                style: {
                                                    width: cell.column.width,
                                                    maxWidth:
                                                        cell.column.maxWidth,
                                                    minWidth:
                                                        cell.column.minWidth,
                                                },
                                            })}
                                        >
                                            {cell.render('Cell')}
                                        </Td>
                                    );
                                })}
                            </Tr>
                        );
                    })}
                </Tbody>
            </Table>
            <Paginator
                canNextPage={canNextPage}
                canPreviousPage={canPreviousPage}
                currentPageIndex={pageIndex}
                gotoPage={gotoPage}
                nextPage={nextPage}
                numberOfPages={pageCount}
                previousPage={previousPage}
                pageOptions={pageOptions}
                pageSize={pageSize}
                setPageSize={setPageSize}
            />
            <Button
                float='right'
                mx={5}
                onClick={startDownload}
                isLoading={loading}
            >
                Skriv ut
            </Button>
        </div>
    );
}

export default RemarksTable;
